import React, { useState, useEffect } from 'react';
import {
    Grid,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Container,
    Button,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TableHead,
    Input,
    Chip,
    Dialog,
    CircularProgress,
    Typography,
    Box

} from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { uniqBy, filter, uniq } from 'lodash';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import Dimmer from '+/Dimmer';
import { DownloadCSVButton } from '+/components/DownloadCsv';
import { useReadAmenityNotes, useReadAllAmenityNotes } from '~/orders';

dayjs.extend(isBetween);

const styles = {
    chips: {
        display: 'flex',
        flexWrap: 'wrap'
    },
    chip: {
        margin: 2
    }
};

const initialState = {
    amenityName: [],
    startDate: null,
    endDate: null,
    building: []
};


const AmenityReport = props => {

    const { match } = props;
    const { property_id } = match.params;

    const [filteredData, setFilteredData] = useState(null);
    const [formState, setFormState] = useState(initialState);
    const [downloadCSV, setDownloadCSV] = useState(false);
    const [downloadDone, setDownloadDone] = useState(false);

    const { data, isFetching } = useReadAmenityNotes(property_id);
    const { data: allAmenityNotes } = useReadAllAmenityNotes({
        enabled: downloadCSV,
        onSuccess: () => {
            setDownloadCSV(false);
            setDownloadDone(true);
        }
    });

    const handleFormChange = (field, value) => {
        setFormState(currentState => ({
            ...currentState,
            [field]: value
        }));
    };

    const handleDownload = () => {

        setDownloadCSV(true);
    };


    useEffect(() => {

        if (data) {

            setFilteredData(filterData(addBuildingPropertyToData(data.inEntrata), formState));
        }
    }, [formState, data]);


    if (isFetching || downloadCSV || !data) return <Dimmer />;

    const amenityNames = getUniqueAmenityNames(data.inEntrata);
    const unitPrefixes = getUniqueUnitPrefixes(data.inEntrata);

    return (
        <div>
            <Container>
                <Typography variant='h4' color='secondary'>{data.property.name}</Typography>
                <Grid alignItems='center' container spacing={4}>
                    <Grid item xs={3}>
                        <FormControl fullWidth>
                            <InputLabel>Amenity Name</InputLabel>
                            <Select
                                multiple
                                multiline
                                input={<Input />}
                                renderValue={selected => (
                                    <div style={styles.chips}>
                                        {selected.map(value => (
                                            <Chip key={value} label={value} style={styles.chip} />
                                        ))}
                                    </div>
                                )}
                                value={formState.amenityName}
                                onChange={e => handleFormChange('amenityName', e.target.value)}
                            >
                                {amenityNames.map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl fullWidth>
                            <InputLabel>Building</InputLabel>
                            <Select
                                multiple
                                multiline
                                input={<Input id='select-multiple-chip' />}
                                renderValue={selected => (
                                    <div style={styles.chips}>
                                        {selected.map(value => (
                                            <Chip key={value} label={value} style={styles.chip} />
                                        ))}
                                    </div>
                                )}
                                value={formState.building}
                                onChange={e => handleFormChange('building', e.target.value)}
                            >
                                {unitPrefixes.map(bldg => <MenuItem key={bldg} value={bldg}>{bldg}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                        <KeyboardDatePicker
                            value={formState.startDate}
                            onChange={value => handleFormChange('startDate', value)}
                            disableToolbar
                            fullWidth
                            variant='inline'
                            format='MM/dd/yyyy'
                            id='start-date-picker-inline'
                            label='Start Date (Date Paid)'
                            KeyboardButtonProps={{
                                'aria-label': 'change date'
                            }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <KeyboardDatePicker
                            value={formState.endDate}
                            onChange={value => handleFormChange('endDate', value)}
                            disableToolbar
                            fullWidth
                            variant='inline'
                            format='MM/dd/yyyy'
                            id='end-date-picker-inline'
                            label='End Date (Date Paid)'
                            KeyboardButtonProps={{
                                'aria-label': 'change date'
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid container justifyContent='space-between' spacing={2}>
                    <Grid item>
                        <Button variant='contained' size='small' onClick={handleDownload}>
                            Download CSV
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button size='small' variant='contained' onClick={() => setFormState(initialState)}>
                            Reset Filters
                        </Button>
                    </Grid>
                </Grid>

                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Building</TableCell>
                            <TableCell>Amenity</TableCell>
                            <TableCell>Count</TableCell>
                            <TableCell>Total</TableCell>
                            <TableCell>Percentage</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {aggregateData(data.inEntrata, filteredData).map((row, i) => (
                            <TableRow key={i}>
                                <TableCell>{row.building}</TableCell>
                                <TableCell>{row.amenity_name}</TableCell>
                                <TableCell>{row.count}</TableCell>
                                <TableCell>{row.total}</TableCell>
                                <TableCell><CircularProgressWithLabel value={((row.count / row.total) * 100)} /></TableCell>
                                {/* <TableCell>{((row.count / row.total) * 100).toFixed(0)}%</TableCell> */}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Container>

            <Dialog open={downloadDone} onClose={() => setDownloadDone(false)}>
                <DownloadCSVButton
                    data={aggregateData(addBuildingPropertyToData(allAmenityNotes || []), addBuildingPropertyToData(allAmenityNotes || []))}
                    filename={`AmenityReport-${dayjs().format('MM/DD/YYYY').toString()}`}
                    excludeColParams={columnsToHide}
                />
            </Dialog>
        </div>
    );
};

const addBuildingPropertyToData = data => data.map(order => {
    if (order.unit_space_name.includes('-')) {
        order.building = order.unit_space_name.split('-')[0];
    }
    else {
        order.building = '1';
    }

    return order;
});

const getUnitPrefixes = orders => orders.map(order => order.building);
const removeDuplicateUnitPrefixes = unitPrefixes => uniq(unitPrefixes);
const getUniqueUnitPrefixes = orders => {
    const unitPrefixes = getUnitPrefixes(orders);

    return removeDuplicateUnitPrefixes(unitPrefixes);
};

const removeDuplicateAmenityNames = orders => uniqBy(orders, 'amenity_name');
const flattenAmenityArray = orders => orders.map(order => order.amenity_name);
const getUniqueAmenityNames = orders => {
    const duplicatesRemoved = removeDuplicateAmenityNames(orders);

    return flattenAmenityArray(duplicatesRemoved);
};

const filterData = (data, formState) => filter(data, item => {

    const nameMatch = formState.amenityName.length === 0 || formState.amenityName.includes(item.amenity_name);
    const bldgMatch = formState.building.length === 0 || formState.building.includes(item.building);
    const filterByDate = formState.startDate && formState.endDate;

    if (filterByDate) {

        if (!item.date_paid) {
            return false;
        }

        return dayjs(item.date_paid).isBetween(formState.startDate, formState.endDate) && nameMatch && bldgMatch;
    }

    return nameMatch && bldgMatch;
});

const aggregateData = (data, filteredData) => {

    const aggregated = (filteredData || []).reduce((acc, order) => {
        if (acc[`${order.building}-${order.amenity_name}`]) {
            acc[`${order.building}-${order.amenity_name}`].count++;
        }
        if (!acc[`${order.building}-${order.amenity_name}`]) {
            acc[`${order.building}-${order.amenity_name}`] = {
                ...order,
                count: 1,
                total: (data || []).filter(i => i.amenity_name === order.amenity_name).length
            };
        }

        return acc;
    }, {});

    return Object.values(aggregated);
};

const columnsToHide = ['lookup_code', 'unit_space_name', 'description', 'amenity_type', 'amenity_group', 'show_on_website', 'amenity_count', 'current_average', 'total_current', 'amenity_rent', 'lease_term', 'charge_code', 'id', 'property_id', 'manager_notes', 'owner_notes', 'ordered', 'paid', 'date_paid', 'check_number', 'created_at', 'updated_at'];

const CircularProgressWithLabel = props => (
    <Box position='relative' display='inline-flex'>
        <CircularProgress variant='determinate' {...props} />
        <Box
            top={0}
            left={0}
            bottom={0}
            right={0}
            position='absolute'
            display='flex'
            alignItems='center'
            justifyContent='center'
        >
            <Typography variant='caption' component='div' color='textSecondary'>{`${Math.round(
                props.value
            )}%`}</Typography>
        </Box>
    </Box>
);


export default AmenityReport;

